import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { FileText, Download, Upload, Trash, Pencil, X, Folder } from '@phosphor-icons/react';
import DropZone from '../DropZone';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { useSearch } from '../VerticalTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'fiche_paie', label: 'Fiche de paie' },
  { value: 'contrat', label: 'Contrat de travail' },
  { value: 'attestation', label: 'Attestation' },
  { value: 'autre', label: 'Autre document' }
];

export const AdminDocsTab = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [formData, setFormData] = useState({ user_id: '', title: '', category: 'autre' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const docsRes = await axios.get(`${API_URL}/api/documents`, { withCredentials: true });
      setDocuments(docsRes.data);
      if (isAdmin) {
        const empRes = await axios.get(`${API_URL}/api/users/employees`, { withCredentials: true });
        setEmployees(empRes.data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !formData.user_id) {
      toast.error('Veuillez selectionner un fichier et un employe');
      return;
    }
    setUploading(true);
    const data = new FormData();
    data.append('file', selectedFile);
    data.append('user_id', formData.user_id);
    data.append('title', formData.title || selectedFile.name);
    data.append('category', formData.category);
    try {
      await axios.post(`${API_URL}/api/documents`, data, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploade');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateTitle = async () => {
    if (!editDoc) return;
    try {
      await axios.put(`${API_URL}/api/documents/${editDoc.id}`, { title: formData.title, category: formData.category }, { withCredentials: true });
      toast.success('Document mis a jour');
      setEditDoc(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`, { withCredentials: true });
      toast.success('Document supprime');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/download/${doc.id}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || doc.title;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du telechargement');
    }
  };

  const resetForm = () => { setFormData({ user_id: '', title: '', category: 'autre' }); setSelectedFile(null); };
  const getCategoryLabel = (value) => CATEGORIES.find(c => c.value === value)?.label || value;
  const { searchTerm: adminSearchTerm } = useSearch();

  const filteredDocs = (selectedEmployee && selectedEmployee !== 'all'
    ? documents.filter(d => d.user_id === selectedEmployee)
    : documents).filter(d => {
      if (!adminSearchTerm) return true;
      const s = adminSearchTerm.toLowerCase();
      return (d.title || '').toLowerCase().includes(s) || (d.user_name || '').toLowerCase().includes(s) || (d.category || '').toLowerCase().includes(s);
    });

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="documents-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Administratif</h1>
          <p className="page-subtitle">{isAdmin ? 'Gerez les documents de vos employes' : 'Vos documents administratifs'}</p>
        </div>
        {isAdmin && (
          <button data-testid="upload-doc-btn" className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Upload size={18} /> Ajouter un document
          </button>
        )}
      </div>

      {isAdmin && employees.length > 0 && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span className="label-text">Filtrer par employe :</span>
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger style={{ width: '250px' }}><SelectValue placeholder="Tous les employes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les employes</SelectItem>
                {employees.map((emp) => (<SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>))}
              </SelectContent>
            </Select>
            {selectedEmployee && selectedEmployee !== 'all' && (
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedEmployee('all')}>Reinitialiser</button>
            )}
          </div>
        </div>
      )}

      {filteredDocs.length > 0 ? (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Document</th>{isAdmin && <th>Employe</th>}<th>Categorie</th><th>Date</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredDocs.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <FileText size={24} color="#002FA7" />
                      <div><div style={{ fontWeight: '600' }}>{doc.title}</div><div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{doc.original_filename}</div></div>
                    </div>
                  </td>
                  {isAdmin && <td>{doc.user_name}</td>}
                  <td><span style={{ padding: '4px 12px', backgroundColor: '#E3F2FD', color: '#1565C0', borderRadius: '2px', fontSize: '0.75rem', fontWeight: '600' }}>{getCategoryLabel(doc.category)}</span></td>
                  <td>{format(parseISO(doc.created_at), 'dd/MM/yyyy')}</td>
                  <td>
                    <div className="action-buttons">
                      <button data-testid={`download-doc-${doc.id}`} className="btn btn-primary btn-sm btn-icon" onClick={() => handleDownload(doc)} title="Telecharger"><Download size={16} /></button>
                      {isAdmin && (
                        <>
                          <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditDoc(doc); setFormData({ ...formData, title: doc.title, category: doc.category }); }} title="Modifier"><Pencil size={16} /></button>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(doc.id)} title="Supprimer"><Trash size={16} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state"><Folder size={48} className="empty-state-icon" /><p className="empty-state-text">Aucun document</p></div>
      )}

      {/* Upload Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '480px' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif' }}>Ajouter un document</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <form onSubmit={handleUpload} style={{ padding: '24px' }}>
              <div className="form-group">
                <label className="form-label">Employe *</label>
                <Select value={formData.user_id} onValueChange={(v) => setFormData({...formData, user_id: v})}><SelectTrigger><SelectValue placeholder="Selectionner un employe" /></SelectTrigger><SelectContent>{employees.map((emp) => (<SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>))}</SelectContent></Select>
              </div>
              <div className="form-group">
                <label className="form-label">Categorie *</label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((cat) => (<SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>))}</SelectContent></Select>
              </div>
              <div className="form-group"><label className="form-label">Titre</label><input type="text" className="form-input" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Ex: Fiche de paie Janvier 2026" /></div>
              <div className="form-group">
                <label className="form-label">Fichier *</label>
                <DropZone
                  onFiles={(files) => setSelectedFile(files[0])}
                  multiple={false}
                  allowFolders={false}
                  testId="admin-doc-dropzone"
                  label={selectedFile ? selectedFile.name : 'Glissez un fichier ici ou cliquez'}
                  hint={selectedFile ? `${(selectedFile.size / 1024).toFixed(0)} Ko` : 'PDF, JPG, PNG, DOC...'}
                  compact
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={uploading} style={{ flex: 1 }}>{uploading ? 'Upload...' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Title Modal */}
      {editDoc && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '400px' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif' }}>Modifier le document</h3>
              <button onClick={() => setEditDoc(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <div style={{ padding: '24px' }}>
              <div className="form-group"><label className="form-label">Titre</label><input type="text" className="form-input" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} /></div>
              <div className="form-group">
                <label className="form-label">Categorie</label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((cat) => (<SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>))}</SelectContent></Select>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button className="btn btn-secondary" onClick={() => setEditDoc(null)} style={{ flex: 1 }}>Annuler</button>
                <button className="btn btn-primary" onClick={handleUpdateTitle} style={{ flex: 1 }}>Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
